import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useRepoParams } from '../../hooks/useRepoParams';
import { refToSegment } from '../../utils/refSegment';
import { get, post, del } from '../../api/client';
import './BranchList.css';

interface Branch {
    name: string;
    sha: string;
    isDefault: boolean;
    updatedAt: string;
}

export function BranchListPage() {
    const { owner, repo } = useRepoParams();
    const repoPath = `${owner}/${repo}`;

    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newBranchName, setNewBranchName] = useState('');
    const [baseBranch, setBaseBranch] = useState('');
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        loadBranches();
    }, [repoPath]);

    const loadBranches = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await get<Branch[]>(`/repos/${repoPath}/branches`);
            setBranches(data);
            // 设置默认基础分支：优先 isDefault，否则用第一个分支（避免只有非 main 分支时未选中导致创建按钮一直禁用）
            const defaultBranch = data.find((b) => b.isDefault);
            if (data.length > 0) {
                setBaseBranch(defaultBranch ? defaultBranch.name : data[0].name);
            }
        } catch (err: any) {
            setError(err.message || '加载分支失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBranch = async () => {
        if (!newBranchName.trim() || !baseBranch) return;

        try {
            setCreating(true);
            const base = branches.find((b) => b.name === baseBranch);
            if (!base) {
                setError('基础分支不存在');
                return;
            }

            await post(`/repos/${repoPath}/branches`, {
                name: newBranchName.trim(),
                sha: base.sha,
            });

            setShowCreateModal(false);
            setNewBranchName('');
            await loadBranches();
        } catch (err: any) {
            setError(err.message || '创建分支失败');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteBranch = async (branchName: string) => {
        if (!confirm(`确定要删除分支 "${branchName}" 吗？`)) return;

        try {
            await del(`/repos/${repoPath}/branches/${branchName}`);
            await loadBranches();
        } catch (err: any) {
            setError(err.message || '删除分支失败');
        }
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return '今天更新';
        if (diffDays === 1) return '昨天更新';
        if (diffDays < 7) return `${diffDays} 天前更新`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} 周前更新`;
        return date.toLocaleDateString('zh-CN');
    };

    return (
        <div className="branch-list-page">
            <div className="page-header">
                <div className="header-left">
                    <h1>
                        <Link to={`/${repoPath}`} className="repo-link">{repo}</Link>
                        <span className="separator">/</span>
                        分支
                    </h1>
                    <span className="branch-count">{branches.length} 个分支</span>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
                    <svg viewBox="0 0 16 16" width="16" height="16">
                        <path fill="currentColor" d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Zm7.25-3.25v2.5h2.5a.75.75 0 0 1 0 1.5h-2.5v2.5a.75.75 0 0 1-1.5 0v-2.5h-2.5a.75.75 0 0 1 0-1.5h2.5v-2.5a.75.75 0 0 1 1.5 0Z" />
                    </svg>
                    新建分支
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
                <div className="loading">加载中...</div>
            ) : branches.length === 0 ? (
                <div className="empty-state">
                    <h3>还没有分支</h3>
                    <p>推送代码后将自动创建分支</p>
                </div>
            ) : (
                <div className="branch-list">
                    {branches.map((branch) => (
                        <div key={branch.name} className={`branch-item ${branch.isDefault ? 'default' : ''}`}>
                            <div className="branch-info">
                                <div className="branch-name">
                                    <svg viewBox="0 0 16 16" width="16" height="16">
                                        <path fill="currentColor" d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.493 2.493 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Z" />
                                    </svg>
                                    <Link to={`/${repoPath}/-/tree/${refToSegment(branch.name)}`} className="branch-link">
                                        {branch.name}
                                    </Link>
                                    {branch.isDefault && <span className="default-badge">默认</span>}
                                </div>
                                <div className="branch-meta">
                                    <span className="sha">{branch.sha.substring(0, 7)}</span>
                                    <span className="updated">{formatDate(branch.updatedAt)}</span>
                                </div>
                            </div>
                            <div className="branch-actions">
                                <Link to={`/${repoPath}/-/commits/${refToSegment(branch.name)}`} className="action-btn" title="查看提交">
                                    <svg viewBox="0 0 16 16" width="16" height="16">
                                        <path fill="currentColor" d="M1.643 3.143.427 1.927A.25.25 0 0 1 .427 1.573l1.216-1.216a.25.25 0 0 1 .426.177zM14.358 12.857l1.216 1.216a.25.25 0 0 1 0 .354l-1.216 1.216a.25.25 0 0 1-.426-.177z" />
                                    </svg>
                                </Link>
                                {!branch.isDefault && (
                                    <button
                                        className="action-btn danger"
                                        title="删除分支"
                                        onClick={() => handleDeleteBranch(branch.name)}
                                    >
                                        <svg viewBox="0 0 16 16" width="16" height="16">
                                            <path fill="currentColor" d="M11 1.75V3h2.25a.75.75 0 0 1 0 1.5H2.75a.75.75 0 0 1 0-1.5H5V1.75C5 .784 5.784 0 6.75 0h2.5C10.216 0 11 .784 11 1.75Zm-5.5 0V3h5V1.75a.25.25 0 0 0-.25-.25h-4.5a.25.25 0 0 0-.25.25ZM4.997 6.178a.75.75 0 1 0-1.493.144L4.217 13.5a1.75 1.75 0 0 0 1.743 1.5h4.08a1.75 1.75 0 0 0 1.743-1.5l.713-7.178a.75.75 0 1 0-1.493-.144z" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* 创建分支模态框 */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>新建分支</h2>
                            <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>分支名称</label>
                                <input
                                    type="text"
                                    value={newBranchName}
                                    onChange={(e) => setNewBranchName(e.target.value)}
                                    placeholder="feature/my-feature"
                                />
                            </div>
                            <div className="form-group">
                                <label>基于分支</label>
                                <select value={baseBranch} onChange={(e) => setBaseBranch(e.target.value)}>
                                    {branches.map((b) => (
                                        <option key={b.name} value={b.name}>
                                            {b.name} {b.isDefault ? '(默认)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                取消
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleCreateBranch}
                                disabled={!newBranchName.trim() || !baseBranch || creating}
                                title={!newBranchName.trim() ? '请填写分支名称' : !baseBranch ? '请选择基于分支' : undefined}
                            >
                                {creating ? '创建中...' : '创建分支'}
                            </button>
                            {showCreateModal && !newBranchName.trim() && (
                                <p className="form-hint">请填写分支名称</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BranchListPage;
